import React, { useState, useEffect, useMemo, useRef } from 'react'
import VisitsService from '../../../services/VisitsService'
import Pagination from '../../../components/UI/TableGrid/Pagination'
import SortableTh from './SortableTh'
import { isWorkCompVisitCategory } from '../../../utils/visitUtils'

const OverallVisitsFullscreen = ({ isOpen, onClose, initialPosition, dateFilter = 'year', filters = {}, navigate }) => {
  const [allData, setAllData] = useState([])
  const [loading, setLoading] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [appliedSearchTerm, setAppliedSearchTerm] = useState('')
  const [sortColumn, setSortColumn] = useState('visit_date')
  const [sortDirection, setSortDirection] = useState('desc')
  const [pagination, setPagination] = useState({
    page: 1,
    perPage: 10,
    totalRecords: 0
  })
  const [positionState, setPositionState] = useState(null)
  const originalPositionRef = useRef(null)
  const hasExpandedRef = useRef(false)

  useEffect(() => {
    if (isOpen) {
      fetchVisits()
    }
  }, [isOpen, dateFilter, filters, sortColumn, sortDirection])

  useEffect(() => {
    if (isOpen && initialPosition) {
      hasExpandedRef.current = false
      originalPositionRef.current = initialPosition
      setPositionState(initialPosition)
      const t = setTimeout(() => {
        setPositionState(null)
        hasExpandedRef.current = true
      }, 50)
      return () => clearTimeout(t)
    }
    if (!isOpen) {
      setPositionState(null)
      originalPositionRef.current = null
      hasExpandedRef.current = false
    }
  }, [isOpen, initialPosition])

  const fetchVisits = async () => {
    setLoading(true)
    try {
      const payload = {
        date_filter: dateFilter || 'year',
        ...filters,
        visits_fullscreen: true,
        sort_column: sortColumn || 'visit_date',
        sort_direction: sortDirection || 'desc'
      }
      const response = await VisitsService.getAllVisits(payload)
      if (response && response.visits) {
        setAllData(response.visits)
        setPagination(prev => ({ ...prev, totalRecords: response.visits.length }))
      } else {
        setAllData([])
        setPagination(prev => ({ ...prev, totalRecords: 0 }))
      }
    } catch (error) {
      console.error('Error fetching overall visits:', error)
      setAllData([])
      setPagination(prev => ({ ...prev, totalRecords: 0 }))
    } finally {
      setLoading(false)
    }
  }

  const filteredData = useMemo(() => {
    if (!appliedSearchTerm) return allData
    const term = appliedSearchTerm.toLowerCase()
    return allData.filter(row => {
      const employee = (row.employee || '').toLowerCase()
      const type = (row.type || row.visit_category || '').toLowerCase()
      const visitDate = (row.visit_date || '').toLowerCase()
      const status = (row.status || '').toLowerCase()
      return employee.includes(term) || type.includes(term) || visitDate.includes(term) || status.includes(term)
    })
  }, [allData, appliedSearchTerm])

  const totalRecords = filteredData.length
  const paginatedData = useMemo(() => {
    const start = (pagination.page - 1) * pagination.perPage
    const end = start + pagination.perPage
    return filteredData.slice(start, end)
  }, [filteredData, pagination.page, pagination.perPage])

  useEffect(() => {
    setPagination(prev => (prev.totalRecords !== totalRecords ? { ...prev, totalRecords } : prev))
  }, [totalRecords])

  const handlePaginationChange = (newPagination) => {
    setPagination(newPagination)
  }

  const handleSort = (col) => {
    if (sortColumn === col) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortColumn(col)
      setSortDirection('desc')
    }
    setPagination(prev => ({ ...prev, page: 1 }))
  }

  const handleViewVisit = (row) => {
    const visitId = row.visit_id || row.id
    if (!visitId || !navigate) return
    const category = row.visit_category || row.type || row.screen_type
    const href = isWorkCompVisitCategory(category)
      ? `/visits/emr-details/${visitId}`
      : `/visits/visit-details/${visitId}`
    navigate(href)
  }

  const CloseIcon = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 4L4 12M4 4L12 12" stroke="#1D1917" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )

  const ViewIcon = () => (
    <svg width="18" height="21" viewBox="0 0 20 21" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M11.7683 8.59714C12.7442 9.57297 12.7442 11.1571 11.7683 12.1346C10.7925 13.1105 9.20834 13.1105 8.23084 12.1346C7.255 11.1588 7.255 9.57464 8.23084 8.59714C9.20834 7.61964 10.7917 7.61964 11.7683 8.59714" stroke="#1D2433" strokeOpacity="0.8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path fillRule="evenodd" clipRule="evenodd" d="M2.5 10.3639C2.5 9.81468 2.62667 9.27135 2.87167 8.77052V8.77052C4.13417 6.18968 6.92417 4.53052 10 4.53052C13.0758 4.53052 15.8658 6.18968 17.1283 8.77052V8.77052C17.3733 9.27135 17.5 9.81468 17.5 10.3639C17.5 10.913 17.3733 11.4564 17.1283 11.9572V11.9572C15.8658 14.538 13.0758 16.1972 10 16.1972C6.92417 16.1972 4.13417 14.538 2.87167 11.9572V11.9572C2.62667 11.4564 2.5 10.913 2.5 10.3639Z" stroke="#1D2433" strokeOpacity="0.8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )

  const handleClose = () => {
    const pos = originalPositionRef.current
    if (pos) {
      setPositionState(pos)
      setTimeout(() => {
        onClose()
      }, 300)
    } else {
      onClose()
    }
  }

  if (!isOpen) return null

  const effectivePosition = (isOpen && initialPosition && positionState === null && !hasExpandedRef.current)
    ? initialPosition
    : (positionState != null ? positionState : null)

  return (
    <div
      className="tblviewcard flip fullscreen-card"
      style={{
        position: 'fixed',
        top: effectivePosition ? `${effectivePosition.top}px` : 0,
        left: effectivePosition ? `${effectivePosition.left}px` : 0,
        width: effectivePosition ? `${effectivePosition.width}px` : '100vw',
        height: effectivePosition ? `${effectivePosition.height}px` : '100vh',
        zIndex: 1040,
        backgroundColor: '#fff',
        padding: '20px',
        borderRadius: effectivePosition ? '8px' : 0,
        boxShadow: effectivePosition ? '0 4px 20px rgba(0,0,0,0.3)' : 'none',
        transition: 'all 0.3s ease-in-out'
      }}
    >
      <div className="card mb-0 border-0 shadow-none" style={{ height: '100%', margin: 0 }}>
        <div className="card-header sh_accordionheader">
          <div className="d-flex justify-content-between align-items-center">
            <h6 className="text-md font-weight-bold mb-0">Overall Visits</h6>
            <a className="btn sh_btniconnew" style={{ cursor: 'pointer' }} onClick={handleClose} title="Minimize">
              <CloseIcon />
            </a>
          </div>
        </div>
        <div className="card-body px-0 pt-0 mt-2 pb-2" style={{ height: 'calc(100% - 60px)', overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
          <div className="px-3 pt-2 pb-2 d-flex align-items-center justify-content-end" style={{ borderBottom: '1px solid #e0e0e0' }}>
            <div className="d-flex align-items-center">
              <div className="mr-2">
                <input
                  type="text"
                  placeholder="Patient"
                  value={searchTerm}
                  className="form-control"
                  style={{ minWidth: '200px' }}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      const trimmed = searchTerm.trim()
                      setSearchTerm(trimmed)
                      setAppliedSearchTerm(trimmed)
                      setPagination(prev => ({ ...prev, page: 1 }))
                    }
                  }}
                />
              </div>
              <div className="addAction">
                <div className="btn-group">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => {
                      const trimmed = searchTerm.trim()
                      setSearchTerm(trimmed)
                      setAppliedSearchTerm(trimmed)
                      setPagination(prev => ({ ...prev, page: 1 }))
                    }}
                  >
                    Search
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => {
                      setSearchTerm('')
                      setAppliedSearchTerm('')
                      setPagination(prev => ({ ...prev, page: 1 }))
                    }}
                  >
                    Clear
                  </button>
                </div>
              </div>
            </div>
          </div>
          {loading ? (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
              <div className="spinner-border text-primary" role="status">
                <span className="sr-only">Loading...</span>
              </div>
            </div>
          ) : (
            <>
              <div style={{ flex: 1, overflow: 'auto' }}>
                <table className="table dataTable sh_tablenew nowrap table-hover" width="100%" cellSpacing="0" style={{ width: '100%' }}>
                  <thead>
                    <tr>
                      <SortableTh label="EMPLOYEE" sortKey="employee" currentSortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                      <SortableTh label="TYPE" sortKey="type" currentSortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                      <SortableTh label="VISIT DATE" sortKey="visit_date" currentSortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                      <SortableTh label="STATUS" sortKey="status" currentSortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                      <th>ACTION</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedData.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="text-center py-3">No visits found</td>
                      </tr>
                    ) : (
                      paginatedData.map((row, index) => (
                        <tr key={row.visit_id || row.id || index}>
                          <td className="forTable_textEllipsis">{row.employee || '-'}</td>
                          <td className="forTable_textEllipsis">{row.type || row.visit_category || '-'}</td>
                          <td className="forTable_textEllipsis">{row.visit_date || '-'}</td>
                          <td className="forTable_textEllipsis">{row.status || '-'}</td>
                          <td className="forTable_textEllipsis">
                            <a className="btn btn-icon px-1 btn-transparent-dark" title="View Visit" style={{ cursor: 'pointer' }} onClick={() => handleViewVisit(row)}>
                              <ViewIcon />
                            </a>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              {totalRecords > 0 && totalRecords > pagination.perPage && (
                <div className="px-3 pb-2" style={{ borderTop: '1px solid #e0e0e0' }}>
                  <Pagination
                    pagination={{ ...pagination, totalRecords }}
                    fetchTableRecords={handlePaginationChange}
                    tableRows={{ data: paginatedData }}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default OverallVisitsFullscreen
